import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  AppBar,
  Toolbar,
  CircularProgress,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import SettingsIcon from '@mui/icons-material/Settings';

const drawerWidth = 240;

const version = "20250618 10:37, 연령별 말투교정";
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://research.pokoro.ai/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isStatus?: boolean;  // 상태 메시지 여부를 나타내는 플래그 추가
}

interface Conversation {
  id: number;
  title: string;
  is_favorite: boolean;
}

interface SaveChatResponse {
  conversation_id: number;
  title: string;
}

interface UserSettings {
  name: string | null;
  birth_year: number | null;
  gender: string | null;
}

// 마크다운 컴포넌트 타입 정의
interface MarkdownContentProps {
  content: string;
}

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <Typography style={{ margin: '0.5em 0' }}>{children}</Typography>,
      h1: ({ children }) => <Typography variant="h4" style={{ margin: '0.5em 0' }}>{children}</Typography>,
      h2: ({ children }) => <Typography variant="h5" style={{ margin: '0.5em 0' }}>{children}</Typography>,
      h3: ({ children }) => <Typography variant="h6" style={{ margin: '0.5em 0' }}>{children}</Typography>,
      ul: ({ children }) => <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>{children}</ul>,
      ol: ({ children }) => <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>{children}</ol>,
      li: ({ children }) => <li style={{ margin: '0.2em 0' }}>{children}</li>,
      code: ({ node, inline, className, children, ...props }: CodeProps) => (
        inline ?
          <code style={{ backgroundColor: '#f5f5f5', padding: '0.2em 0.4em', borderRadius: '3px' }} {...props}>{children}</code> :
          <pre style={{ backgroundColor: '#f5f5f5', padding: '1em', borderRadius: '5px', overflowX: 'auto' }}>
            <code {...props}>{children}</code>
          </pre>
      ),
      blockquote: ({ children }) => (
        <blockquote style={{
          borderLeft: '4px solid #ccc',
          margin: '1em 0',
          paddingLeft: '1em',
          color: '#666'
        }}>
          {children}
        </blockquote>
      ),
    }}
  >
    {content}
  </ReactMarkdown>
);

function App() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConversationForMenu, setSelectedConversationForMenu] = useState<Conversation | null>(null);
  const [isHovering, setIsHovering] = useState<number | null>(null);
  const [titleDialogOpen, setTitleDialogOpen] = useState<boolean>(false);
  const [newTitle, setNewTitle] = useState<string>('');
  const [favorites, setFavorites] = useState<number[]>([]);
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);
  const lastUserScrollTime = useRef<number>(0);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [userSettings, setUserSettings] = useState<UserSettings>({
    name: null,
    birth_year: null,
    gender: null
  });
  const [ageAnchorEl, setAgeAnchorEl] = useState<null | HTMLElement>(null);
  const [genderAnchorEl, setGenderAnchorEl] = useState<null | HTMLElement>(null);

  // 사용자의 스크롤 액션 감지
  const handleUserScroll = (event: Event | React.KeyboardEvent) => {
    // 마우스 휠 이벤트
    if (event.type === 'wheel') {
      lastUserScrollTime.current = Date.now();
      setShouldAutoScroll(false);
      return;
    }

    // 키보드 이벤트 (화살표 키)
    if (event.type === 'keydown' && event instanceof KeyboardEvent &&
        (event.key === 'ArrowUp' ||
         event.key === 'ArrowDown' ||
         event.key === 'PageUp' ||
         event.key === 'PageDown')) {
      lastUserScrollTime.current = Date.now();
      setShouldAutoScroll(false);
      return;
    }

    // 스크롤 이벤트
    if (event.type === 'scroll') {
      const container = messagesBoxRef.current;
      if (!container) return;

      // 마지막 사용자 스크롤로부터 100ms가 지났고, 스크롤이 맨 아래에 있다면
      const isAtBottom = container.scrollHeight - container.scrollTop === container.clientHeight;
      const isUserScroll = Date.now() - lastUserScrollTime.current < 100;

      if (!isUserScroll && isAtBottom) {
        setShouldAutoScroll(true);
      }
    }
  };

  // 포커스 설정 함수
  const focusInput = () => {
    const inputElement = document.querySelector('.MuiTextField-root textarea');
    if (inputElement instanceof HTMLElement) {
      setTimeout(() => {
        inputElement.focus();
      }, 100);
    }
  };

  // 맨 아래로 스크롤
  const scrollToBottom = () => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    // 메시지가 변경될 때마다 스크롤
    scrollToBottom();
  }, [messages, shouldAutoScroll]);

  useEffect(() => {
    // 이벤트 리스너 등록
    const messageBox = messagesBoxRef.current;
    if (messageBox) {
      messageBox.addEventListener('wheel', handleUserScroll, { passive: true });
      messageBox.addEventListener('scroll', handleUserScroll, { passive: true });
      document.addEventListener('keydown', handleUserScroll as unknown as EventListener);

      return () => {
        messageBox.removeEventListener('wheel', handleUserScroll);
        messageBox.removeEventListener('scroll', handleUserScroll);
        document.removeEventListener('keydown', handleUserScroll as unknown as EventListener);
      };
    }
  }, []);

  useEffect(() => {
    // 컴포넌트 마운트 시 입력창에 포커스
    focusInput();
  }, []);

  useEffect(() => {
    fetchConversations().then((fetchedConversations) => {
      if (fetchedConversations && fetchedConversations.length > 0) {
        handleConversationSelect(fetchedConversations[0]);
      }
    });
  }, []);

  const fetchConversations = async (): Promise<Conversation[]> => {
    try {
      const response = await axios.get<{ client_id: string; conversations: Conversation[] }>(`${API_BASE_URL}/conversations`);

      // 새로 받은 client_id를 저장
      localStorage.setItem('x-client-id', response.data.client_id);

      setConversations(response.data.conversations);
      return response.data.conversations;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  };

  // axios 인터셉터 설정
  useEffect(() => {
    // 요청 인터셉터
    axios.interceptors.request.use((config) => {
      const clientId = localStorage.getItem('x-client-id');
      if (clientId) {
        config.headers['X-Client-ID'] = clientId;
      }
      return config;
    });
  }, []);

  const handleConversationSelect = async (conversation: Conversation) => {
    setCurrentConversation(conversation);
    try {
      const response = await axios.get<{ messages: Message[] }>(`${API_BASE_URL}/conversations/${conversation.id}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  const handleNewSession = async () => {
    try {
      const response = await axios.post<{ new_session: Conversation; all_sessions: Conversation[] }>(`${API_BASE_URL}/conversations/new`);
      const { new_session, all_sessions } = response.data;

      setConversations(all_sessions);
      setCurrentConversation(new_session);
      setMessages([]);
    } catch (error) {
      console.error('Error creating new session:', error);
      alert('새 세션 생성 중 오류가 발생했습니다.');
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, conversation: Conversation) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversationForMenu(conversation);
  };

  const handleMenuClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchorEl(null);
    setSelectedConversationForMenu(null);
  };

  const handleTitleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedConversationForMenu) {
      setNewTitle(selectedConversationForMenu.title);
      setTitleDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleTitleSave = async () => {
    try {
      if (selectedConversationForMenu) {
        await axios.put(`${API_BASE_URL}/conversations/${selectedConversationForMenu.id}/title`, {
          title: newTitle
        });
        fetchConversations();
        setTitleDialogOpen(false);
      }
    } catch (error) {
      console.error('Error updating title:', error);
      alert('타이틀 변경 중 오류가 발생했습니다.');
    }
  };

  const handleFavoriteToggle = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      if (selectedConversationForMenu) {
        await axios.put(`${API_BASE_URL}/conversations/${selectedConversationForMenu.id}/favorite`);
        fetchConversations();
        handleMenuClose();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('즐겨찾기 상태 변경 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteConversation = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('정말로 이 대화를 삭제하시겠습니까?')) {
      try {
        if (!selectedConversationForMenu || !conversations.length) return;

        // 삭제할 세션의 인덱스를 찾습니다
        const currentIndex = conversations.findIndex(conv => conv.id === selectedConversationForMenu.id);

        // 세션을 삭제합니다
        await axios.delete(`${API_BASE_URL}/conversations/${selectedConversationForMenu.id}`);

        // 대화 목록을 새로 가져옵니다
        const updatedConversations = await fetchConversations();

        // 다음 활성화할 세션을 결정합니다
        if (updatedConversations.length > 0) {
          let nextConversation: Conversation;
          if (currentIndex === conversations.length - 1) {
            // 마지막 세션이었다면 이전 세션을 선택
            nextConversation = updatedConversations[currentIndex - 1];
          } else {
            // 그 외의 경우 다음 세션을 선택
            nextConversation = updatedConversations[currentIndex];
          }
          await handleConversationSelect(nextConversation);
        } else {
          // 남은 세션이 없다면 새 세션을 생성
          await handleNewSession();
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
        alert('대화 삭제 중 오류가 발생했습니다.');
      }
    }
    handleMenuClose();
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const newMessage: Message = {
      role: 'user',
      content: input,
    };
    const userInput = input;
    const currentTime = new Date().toISOString();

    setMessages(prev => [...prev, newMessage]);
    setInput('');
    setLoading(true);
    setShouldAutoScroll(true);  // 메시지 전송 시 자동 스크롤 활성화

    try {
      // 스트리밍 응답을 위한 임시 메시지 추가
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'X-Client-ID': localStorage.getItem('x-client-id') || ''
        },
        body: JSON.stringify({
          message: userInput,
          conversation_id: currentConversation?.id || null,
          timestamp: currentTime
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';

      while (reader) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE 형식에 맞게 파싱 (event: 라인과 data: 라인 분리)
        const lines = buffer.split('\n');
        buffer = '';

        let currentEvent: string | null = null;
        let currentData: string | null = null;

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();

          if (line === '') {
            // 빈 줄이 나오면 이벤트 완료
            if (currentEvent && currentData) {
              try {
                const data = JSON.parse(currentData);

                if (data.type === 'status') {
                  // 상태 메시지 업데이트
                  setMessages(prev => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1] = {
                      role: 'assistant',
                      content: data.content,
                      isStatus: true
                    };
                    return newMessages;
                  });
                } else if (data.type === 'chunk') {
                  fullResponse += data.content;
                  // 실시간으로 메시지 업데이트
                  setMessages(prev => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1] = {
                      role: 'assistant',
                      content: fullResponse
                    };
                    return newMessages;
                  });
                } else if (data.type === 'done') {
                  fullResponse = data.content;
                  setMessages(prev => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1] = {
                      role: 'assistant',
                      content: fullResponse
                    };
                    return newMessages;
                  });
                } else if (data.type === 'error') {
                  throw new Error(data.error);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e, 'Data:', currentData);
              }

              currentEvent = null;
              currentData = null;
            }
          } else if (line.startsWith('event:')) {
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.substring(5).trim();
          } else if (i === lines.length - 1 && line !== '') {
            // 마지막 라인이 완전하지 않을 수 있으므로 버퍼에 저장
            buffer = line;
          }
        }
      }

      // 대화 저장
      const saveResponse = await axios.post<SaveChatResponse>(`${API_BASE_URL}/save-chat`, {
        message: userInput,
        response: fullResponse,
        conversation_id: currentConversation?.id
      });

      if (!currentConversation) {
        fetchConversations();
        const newConversationId = saveResponse.data.conversation_id;
        setCurrentConversation({
          id: newConversationId,
          title: saveResponse.data.title,
          is_favorite: false
        });
      } else {
        // 제목이 자동 업데이트된 경우 프론트엔드에도 반영
        if (saveResponse.data.title !== currentConversation.title) {
          setCurrentConversation(prev => prev ? ({
            ...prev,
            title: saveResponse.data.title
          }) : null);
          // conversations 배열도 업데이트
          setConversations(prev => prev.map(conv =>
            conv.id === currentConversation.id
              ? { ...conv, title: saveResponse.data.title }
              : conv
          ));
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);

      // 오류 메시지 표시
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages[newMessages.length - 1]) {
          newMessages[newMessages.length - 1].content = '오류가 발생했습니다: ' + (error instanceof Error ? error.message : String(error));
        }
        return newMessages;
      });

      let errorMessage = '메시지 전송 중 오류가 발생했습니다';
      if (axios.isAxiosError(error) && error.response?.data?.detail) {
        errorMessage += ': ' + error.response.data.detail;
      } else if (error instanceof Error) {
        errorMessage += ': ' + error.message;
      }
      alert(errorMessage);
    } finally {
      setLoading(false);
      // 응답 수신 후 입력창에 포커스 설정
      focusInput();
    }
  };

  // 사용자 설정 불러오기
  const fetchUserSettings = async () => {
    try {
      const response = await axios.get<UserSettings>(`${API_BASE_URL}/user-settings`);
      setUserSettings(response.data);
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  // 컴포넌트 마운트 시 설정 불러오기
  useEffect(() => {
    fetchUserSettings();
  }, []);

  // 설정 저장
  const handleSaveSettings = async (newSettings?: UserSettings) => {
    try {
      const settingsToSave = newSettings || userSettings;
      await axios.put(`${API_BASE_URL}/user-settings`, settingsToSave);
      if (!newSettings) {
        setSettingsOpen(false);
      }
    } catch (error) {
      console.error('Error saving user settings:', error);
      alert('설정 저장 중 오류가 발생했습니다.');
    }
  };

  // 설정 다이얼로그의 저장 버튼 클릭 핸들러
  const handleSettingsDialogSave = () => {
    handleSaveSettings();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap component="div">
            Pokoro - FreeChat ({version})
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* 나이 선택 */}
            <Box component="span">
              <Button
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 'inherit',
                  fontWeight: 'normal',
                  textTransform: 'none',
                  p: '0'
                }}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAgeAnchorEl(event.currentTarget)}
              >
                {userSettings.birth_year ?
                  `${new Date().getFullYear() - userSettings.birth_year}세` :
                  '7세'}
              </Button>
              <Menu
                anchorEl={ageAnchorEl}
                open={Boolean(ageAnchorEl)}
                onClose={() => setAgeAnchorEl(null)}
                PaperProps={{
                  style: {
                    maxHeight: '70vh',
                    overflowY: 'auto'
                  }
                }}
              >
                {Array.from({ length: 93 }, (_, i) => i + 7).map((age) => (
                  <MenuItem
                    key={age}
                    onClick={() => {
                      const newSettings = {
                        ...userSettings,
                        birth_year: new Date().getFullYear() - age
                      };
                      setUserSettings(newSettings);
                      handleSaveSettings(newSettings);
                      setAgeAnchorEl(null);
                    }}
                  >
                    {age}세
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {userSettings.name && (
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                ({userSettings.name}
              </Typography>
            )}

            {/* 성별 선택 */}
            {userSettings.name && (
              <Box component="span">
                <Button
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    minWidth: 'auto',
                    fontSize: 'inherit',
                    fontWeight: 'normal',
                    textTransform: 'none',
                    p: '0'
                  }}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => setGenderAnchorEl(event.currentTarget)}
                >
                  {userSettings.gender ? `, ${userSettings.gender}` : ''}
                </Button>
                <Menu
                  anchorEl={genderAnchorEl}
                  open={Boolean(genderAnchorEl)}
                  onClose={() => setGenderAnchorEl(null)}
                >
                  <MenuItem
                    onClick={() => {
                      const newSettings = { ...userSettings, gender: '남성' };
                      setUserSettings(newSettings);
                      handleSaveSettings(newSettings);
                      setGenderAnchorEl(null);
                    }}
                  >
                    남성
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const newSettings = { ...userSettings, gender: '여성' };
                      setUserSettings(newSettings);
                      handleSaveSettings(newSettings);
                      setGenderAnchorEl(null);
                    }}
                  >
                    여성
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const newSettings = { ...userSettings, gender: '구분 안함' };
                      setUserSettings(newSettings);
                      handleSaveSettings(newSettings);
                      setGenderAnchorEl(null);
                    }}
                  >
                    구분 안함
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {userSettings.name && (
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                )
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Toolbar />
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          <List>
            <ListItem>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewSession}
                sx={{
                  mb: 1,
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                새 세션 열기
              </Button>
            </ListItem>
            <Divider sx={{ my: 1 }} />
            {conversations.map((conversation) => (
              <ListItem
                button
                key={conversation.id}
                onClick={() => handleConversationSelect(conversation)}
                selected={currentConversation?.id === conversation.id}
                onMouseEnter={() => setIsHovering(conversation.id)}
                onMouseLeave={() => setIsHovering(null)}
                sx={{
                  borderRadius: 1,
                  m: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: 'primary.main',
                  },
                  maxWidth: '100%',
                  padding: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  {conversation.is_favorite && (
                    <StarIcon sx={{
                      mr: 1,
                      fontSize: '0.9rem',
                      color: currentConversation?.id === conversation.id ? 'white' : 'primary.main'
                    }} />
                  )}
                  <ListItemText
                    primary={conversation.title}
                    primaryTypographyProps={{
                      noWrap: true,
                      style: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }
                    }}
                  />
                </Box>
                {isHovering === conversation.id && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, conversation)}
                    sx={{
                      ml: 1,
                      color: currentConversation?.id === conversation.id ? 'white' : 'inherit',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          mt: 'auto'
        }}>
          <IconButton
            onClick={() => setSettingsOpen(true)}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              gap: 1
            }}
          >
            <SettingsIcon />
            <Typography>설정</Typography>
          </IconButton>
        </Box>
      </Drawer>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={(event, reason) => {
          handleMenuClose();
        }}
      >
        <MenuItem onClick={handleTitleEditClick}>
          <EditIcon sx={{ mr: 1 }} />
          타이틀 바꾸기
        </MenuItem>
        <MenuItem onClick={handleFavoriteToggle}>
          {selectedConversationForMenu?.is_favorite ? (
            <StarIcon sx={{ mr: 1 }} />
          ) : (
            <StarBorderIcon sx={{ mr: 1 }} />
          )}
          즐겨찾기에 추가
        </MenuItem>
        <MenuItem onClick={handleDeleteConversation}>
          <DeleteIcon sx={{ mr: 1 }} />
          삭제하기
        </MenuItem>
      </Menu>

      <Dialog open={titleDialogOpen} onClose={() => setTitleDialogOpen(false)}>
        <DialogTitle>타이틀 변경</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="새 타이틀"
            type="text"
            fullWidth
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTitleDialogOpen(false)}>취소</Button>
          <Button onClick={handleTitleSave}>저장</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>사용자 설정</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="이름"
            type="text"
            fullWidth
            value={userSettings.name || ''}
            onChange={(e) => setUserSettings(prev => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="태어난 해"
            type="number"
            fullWidth
            value={userSettings.birth_year || ''}
            onChange={(e) => setUserSettings(prev => ({ ...prev, birth_year: parseInt(e.target.value) || null }))}
          />
          <TextField
            margin="dense"
            label="성별"
            select
            fullWidth
            value={userSettings.gender || ''}
            onChange={(e) => setUserSettings(prev => ({ ...prev, gender: e.target.value }))}
          >
            <MenuItem value="">선택 안 함</MenuItem>
            <MenuItem value="남성">남성</MenuItem>
            <MenuItem value="여성">여성</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>취소</Button>
          <Button onClick={handleSettingsDialogSave}>저장</Button>
        </DialogActions>
      </Dialog>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              ref={messagesBoxRef}
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                '& > div': {
                  maxWidth: '80%',
                  mb: 2,
                  '& > .MuiPaper-root': {
                    width: 'fit-content',
                    maxWidth: '100%'
                  }
                },
              }}
            >
              {messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    ml: message.role === 'user' ? 'auto' : 0,
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    width: '100%'
                  }}
                >
                  {message.isStatus ? (
                    <Box
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.85rem',
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      {message.content}
                    </Box>
                  ) : message.content.length > 0 ? (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                        color: message.role === 'user' ? 'white' : 'text.primary',
                      }}
                    >
                      <ReactMarkdown
                        children={message.content}
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({children}) => (
                            <p style={{ margin: 0 }}>{children}</p>
                          ),
                          code: ({node, inline, className, children, ...props}: any) => {
                            const match = /language-(\w+)/.exec(className || '')
                            return !inline ? (
                              <Paper elevation={0} sx={{ bgcolor: 'grey.100' }}>
                                <pre style={{ padding: '1em', overflowX: 'auto', margin: 0 }}>
                                  <code className={className} {...props}>
                                    {children}
                                  </code>
                                </pre>
                              </Paper>
                            ) : (
                              <code className={className} {...props}>
                                {children}
                              </code>
                            )
                          }
                        }}
                      />
                    </Paper>
                  ) : null}
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>
            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="메시지를 입력하세요..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      setShouldAutoScroll(true);  // 엔터키로 전송 시 자동 스크롤 활성화
                      handleSend();
                    }
                  }}
                  disabled={loading}
                  multiline
                  maxRows={4}
                  className="MuiTextField-root"
                />
                <Button
                  variant="contained"
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handleSend}
                  disabled={loading}
                />
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

export default App;